import { Skeleton } from '@mui/material';
import { FC, useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import Button from 'src/components/ui/Button/Button';
import CustomerDetails from 'src/components/ui/CustomerDetails/CustomerDetails';
import { FormAutocomplete } from 'src/components/ui/FormAutocomplete/FormAutocomplete';
import Input from 'src/components/ui/Input/Input';
import PageHeader from 'src/components/ui/PageHeader/PageHeader';
import { routes } from 'src/routes';
import {
  getServiceAccountList,
  setServiceAccountList,
} from 'src/store/actions/serviceAccountActions';
import {
  getTicketDetails,
  reassignTicket,
  setTicketDetails,
} from 'src/store/actions/ticketActions';
import { ServiceAccount } from 'src/store/models/ServiceAccount';
import { TicketStatus } from 'src/store/models/Ticket';
import { getDistanceFromLatLonInKm } from 'src/utils/addressUtils';
import { useLoadingSelector } from 'src/utils/useLoadingSelector';
import { useNonInitialEffect } from 'src/utils/useNonInitialEffect';

import classes from './ReassignTicket.module.scss';

export interface ReassignTicketFormData {
  assignee: ServiceAccount;
}

const defaultValues: ReassignTicketFormData = {
  assignee: null,
};

const ReassignTicket: FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();

  const { control, handleSubmit, formState } = useForm<ReassignTicketFormData>({
    mode: 'onChange',
    defaultValues,
  });

  const loading = useLoadingSelector({
    actionType: getTicketDetails.toString(),
  });
  const ticket = useAppSelector((state) => state.ticket.ticketDetails);
  const serviceAccountList = useAppSelector(
    (state) => state.serviceAccount.serviceAccountList
  );
  const loadingList = useLoadingSelector({
    actionType: getServiceAccountList.toString(),
  });

  useEffect(() => {
    dispatch(setTicketDetails(null));
    dispatch(setServiceAccountList({ count: 0, data: [], ctx: null }));
    dispatch(getTicketDetails({ id: params?.ticketId }));
    dispatch(getServiceAccountList({ params: { size: 1000 } }));
    return () => {
      dispatch(setTicketDetails(null));
    };
    // eslint-disable-next-line
  }, []);

  useNonInitialEffect(() => {
    if (
      ticket?.status &&
      (ticket?.status === TicketStatus.UNASSIGNED ||
        ticket?.status === TicketStatus.RESOLVED)
    ) {
      navigate(routes.TICKETS, { replace: true });
    }
    // eslint-disable-next-line
  }, [ticket]);

  const onSubmit: SubmitHandler<ReassignTicketFormData> = (formData) => {
    dispatch(
      reassignTicket({ ticketId: ticket?.id, service: formData?.assignee })
    );
  };

  return (
    <div className={classes.Container}>
      <PageHeader
        title={`${t('tickets:reassign_ticket')} ${ticket?.ticketId || ''}`}
        showBackButton
        backConfirmationText={
          formState.isDirty && t('tickets:changes_will_not_save_reassign')
        }
        endNode={
          <Button
            type="submit"
            form="reassign-ticket-form"
            className={classes.HeaderButton}
            disabled={!formState.isDirty || !ticket}
          >
            {t('tickets:reassign')}
          </Button>
        }
      />
      <form
        noValidate
        id="reassign-ticket-form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <section>
          <h4>{`${t('tickets:current_assignee')}:`}</h4>
          {!loading ? (
            <Input
              value={`${ticket?.assignee?.company?.title} / ${
                ticket?.assignee?.title
              } / ${t('tickets:km_away', {
                number: getDistanceFromLatLonInKm(
                  parseFloat(ticket?.assignee?.company?.lat),
                  parseFloat(ticket?.assignee?.company?.lon),
                  parseFloat(ticket?.user?.lat),
                  parseFloat(ticket?.user?.lon)
                ).toFixed(1),
              })}`}
              label={t('common:assignee')}
              disabled
              fullWidth
            />
          ) : (
            <Skeleton />
          )}
        </section>
        <section>
          <h4>{`${t('tickets:reassign_to')}:`}</h4>
          {!loadingList ? (
            <FormAutocomplete<ReassignTicketFormData, ServiceAccount>
              name="assignee"
              control={control}
              AutocompleteProps={{
                getOptionLabel: (option) =>
                  `${option?.company?.title} / ${option?.title} / ${t(
                    'tickets:km_away',
                    {
                      number: getDistanceFromLatLonInKm(
                        parseFloat(option?.company?.lat),
                        parseFloat(option?.company?.lon),
                        parseFloat(ticket?.user?.lat),
                        parseFloat(ticket?.user?.lon)
                      ).toFixed(1),
                    }
                  )}`,
                isOptionEqualToValue: (option, value) =>
                  option?.id === value?.id,
                options:
                  serviceAccountList?.data?.filter(
                    (s) => s?.enabled && s?.eMail !== ticket?.assignee?.eMail
                  ) || [],
                label: t('common:assignee'),
                placeholder: `${t('common:choose')}...`,
              }}
            />
          ) : (
            <Skeleton />
          )}
          <p>
            {`${t('ticket_details:cant_find_service')} `}
            <Link to={routes.NEW_SERVICE_ACCOUNT}>
              {t('ticket_details:create_new_service')}
            </Link>
          </p>
        </section>
        <CustomerDetails
          ticket={ticket}
          className={classes.CustomerDetails}
          loading={loading}
        />
      </form>
    </div>
  );
};

export default ReassignTicket;
